import React, { useEffect, useState } from 'react';
import MessageList from './MessageList';
import ChatInput   from './ChatInput';
import { useJarvis } from '../../hooks/useJarvis';
import { useJarvisContext } from '../../context/JarvisContext';
import { THEME } from '../../styles/theme';
import { isMuted, toggleMute } from '../../services/voiceService';

const S = {
  screen: {
    display:       'flex',
    flexDirection: 'column',
    flex:          1,
    height:        '100%',
    overflow:      'hidden',
  },
  topBanner: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    padding:        '8px 16px',
    borderBottom:   '1px solid rgba(255,255,255,0.05)',
    background:     'rgba(0,0,0,0.2)',
    flexShrink:     0,
  },
  bannerLeft: {
    display:    'flex',
    alignItems: 'center',
    gap:        '8px',
  },
  onlineDot: {
    width:        '8px',
    height:       '8px',
    borderRadius: '50%',
    background:   THEME.colors.green,
    boxShadow:    `0 0 6px ${THEME.colors.green}`,
    animation:    'dotPulse 2s ease-in-out infinite',
  },
  onlineLabel: {
    fontSize:     '11px',
    color:        THEME.colors.green,
    letterSpacing: '2px',
    fontFamily:   THEME.fonts.hud,
    fontWeight:   '700',
  },
  clearBtn: {
    background:   'none',
    border:       `1px solid rgba(255,255,255,0.1)`,
    borderRadius: '6px',
    color:        THEME.colors.textMuted,
    fontSize:     '12px',
    padding:      '4px 10px',
    cursor:       'pointer',
    letterSpacing: '1px',
  },
  errorBanner: {
    padding:    '10px 16px',
    background: 'rgba(255,51,51,0.1)',
    borderBottom: '1px solid rgba(255,51,51,0.2)',
    display:    'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  errorText: {
    fontSize: '13px',
    color:    THEME.colors.red,
  },
  errorClose: {
    background: 'none',
    border:     'none',
    color:      THEME.colors.red,
    cursor:     'pointer',
    fontSize:   '16px',
  },
  muteBtn: (muted) => ({
    background:   muted ? 'rgba(255,51,51,0.1)' : 'rgba(0,212,255,0.1)',
    border:       `1px solid ${muted ? 'rgba(255,51,51,0.3)' : 'rgba(0,212,255,0.25)'}`,
    borderRadius: '6px',
    color:        muted ? THEME.colors.red : THEME.colors.blue,
    fontSize:     '13px',
    padding:      '4px 8px',
    cursor:       'pointer',
    display:      'flex',
    alignItems:   'center',
  }),
};

export default function ChatScreen() {
  useJarvisContext();
  const { sendMessage, isThinking, lastError, clearError, clearMessages, messages, runBrainAnalysis } = useJarvis();
  const [muted, setMuted] = useState(isMuted());
  const handleToggleMute = () => setMuted(toggleMute());

  useEffect(() => {
    const t = setTimeout(() => runBrainAnalysis(), 2000);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Read pending message sent from BottomBar (cross-screen navigation)
  useEffect(() => {
    const pending = localStorage.getItem('jarvis_pending_message');
    if (pending) {
      localStorage.removeItem('jarvis_pending_message');
      sendMessage(pending);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = (text, screenshot) => {
    sendMessage(text, screenshot);
  };

  return (
    <div style={S.screen}>
      <style>{`
        @keyframes dotPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      {/* Status bar */}
      <div style={S.topBanner}>
        <div style={S.bannerLeft}>
          <div style={S.onlineDot} />
          <span style={S.onlineLabel}>GROQ AI ONLINE</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: THEME.colors.textMuted }}>
            {messages.length} message{messages.length !== 1 ? 's' : ''}
          </span>
          <button style={S.muteBtn(muted)} onClick={handleToggleMute} title={muted ? 'Unmute JARVIS voice' : 'Mute JARVIS voice'}>
            {muted ? '🔇' : '🔊'}
          </button>
          {messages.length > 0 && (
            <button style={S.clearBtn} onClick={clearMessages}>CLEAR</button>
          )}
        </div>
      </div>

      {/* Error banner */}
      {lastError && (
        <div style={S.errorBanner}>
          <span style={S.errorText}>⚠️ {lastError}</span>
          <button style={S.errorClose} onClick={clearError}>✕</button>
        </div>
      )}

      {/* Messages */}
      <MessageList messages={messages} isThinking={isThinking} />

      {/* Input */}
      <ChatInput onSend={handleSend} isThinking={isThinking} />
    </div>
  );
}
